import { memo, useEffect, useMemo, useRef } from 'react';

import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSuperState } from 'hooks';

import 'components/GMetrixTreeView/gmetrix-tree-view.scss';

const ROOT_KEY = '__gmtv_root__' as const;

const DEFAULT_LOADING_DELAY_MS = 200;

const DEFAULT_SEARCH_DEBOUNCE_MS = 250;

const MAX_INDENT_DEPTH = 5;

export type TreeNodeId = string | number;

export type GMetrixTreeNode = {
    id: TreeNodeId;
    children?: GMetrixTreeNode[];

    /** Hint that this node has children that should be lazy-loaded on first expand. */
    hasChildren?: boolean;

    label: React.ReactNode;
};

export type GMetrixTreeViewSelectionMode = 'multiple' | 'single' | 'leaf-single' | 'none';

export type GMetrixTreeViewProps = {
    className?: string;

    /** Ids that should start expanded. Omit to expand everything currently loaded. */
    defaultExpandedIds?: TreeNodeId[];
    emptyMessage?: React.ReactNode;

    /**
     * Returns the text that should be matched against the search query for a given node.
     * Defaults to the node's label when it's a string, otherwise the stringified id.
     */
    getSearchText?: (node: GMetrixTreeNode) => string;

    /** Called the first time a lazy node is expanded (or selected in multi-select). */
    loadChildren?: (node: GMetrixTreeNode) => Promise<GMetrixTreeNode[]>;

    /** Suppress the loading row if `loadChildren` resolves faster than this. Default 200ms. */
    loadingDelayMs?: number;
    maxHeight?: string | number;

    nodes: GMetrixTreeNode[];

    /** Rendered inside the tree when a search yields zero matches. */
    noResultsMessage?: React.ReactNode;

    onSelectionChange?: (ids: TreeNodeId[]) => void;
    renderLoadErrorNode?: (node: GMetrixTreeNode, error: Error, retry: () => void) => React.ReactNode;
    renderLoadingNode?: (node: GMetrixTreeNode) => React.ReactNode;

    searchDebounceMs?: number;
    searchPlaceholder?: string;

    selectAllLabel?: string;

    selectedIds?: TreeNodeId[];

    selectionMode?: GMetrixTreeViewSelectionMode;

    /** Adds a debounced client-side search input above the tree. */
    showSearch?: boolean;

    /** Multi-select only — adds a synthetic "Select/Deselect All" parent above the supplied nodes. */
    showSelectAll?: boolean;
};

type LoadState =
    | { children: GMetrixTreeNode[]; status: 'loaded' }
    | { status: 'loading' }
    | { error: Error; status: 'error' };

type CheckState = 'checked' | 'unchecked' | 'indeterminate';

type TriCheckProps = {
    name?: string;
    disabled?: boolean;
    label: string;
    onChange: () => void;
    state: CheckState;
    type: 'checkbox' | 'radio';
};

type TreeRowProps = {
    children?: React.ReactNode;
    controlDisabled?: boolean;
    controlType: 'checkbox' | 'radio' | 'none';
    depth: number;
    expandable: boolean;
    expanded?: boolean;
    label: React.ReactNode;
    labelInert?: boolean;
    labelText: string;
    onCheck?: () => void;
    onToggle?: () => void;
    radioGroupName?: string;
    selectedCount?: number;
    selectedCountAlwaysShow?: boolean;
    state: CheckState;
};

const resolveChildren = (
    node: GMetrixTreeNode,
    cache: Map<TreeNodeId, LoadState>
): GMetrixTreeNode[] | undefined => {

    if (node.children) return node.children;
    const cached = cache.get(node.id);
    if (cached?.status === 'loaded') return cached.children;
    return undefined;

};

const isLazyUnloaded = (
    node: GMetrixTreeNode,
    cache: Map<TreeNodeId, LoadState>
): boolean => Boolean(node.hasChildren) && resolveChildren(node, cache) === undefined;

const collectLoadedLeafIds = (
    node: GMetrixTreeNode,
    cache: Map<TreeNodeId, LoadState>,
    out: TreeNodeId[]
) => {

    const children = resolveChildren(node, cache);
    if (!children || children.length === 0) {

        if (!node.hasChildren) out.push(node.id);
        return;

    }
    children.forEach(child => collectLoadedLeafIds(child, cache, out));

};

const collectAllKnownIds = (
    node: GMetrixTreeNode,
    cache: Map<TreeNodeId, LoadState>,
    out: TreeNodeId[]
) => {

    const children = resolveChildren(node, cache);
    if (!children || children.length === 0) {

        out.push(node.id);
        return;

    }
    children.forEach(child => collectAllKnownIds(child, cache, out));

};

const defaultGetSearchText = (node: GMetrixTreeNode): string => (typeof node.label === 'string' ? node.label : String(node.id));

/**
 * Filter the visible tree by `query`. A node is kept when itself matches OR any descendant matches.
 * Lazy-unloaded subtrees are kept (we can't search what we don't have); their parent stays visible.
 * Also returns the set of ids whose subtree contains a match — used to auto-expand into matches.
 */
const filterTree = (
    list: GMetrixTreeNode[],
    cache: Map<TreeNodeId, LoadState>,
    query: string,
    getSearchText: (node: GMetrixTreeNode) => string,
    expandIds: Set<TreeNodeId>
): GMetrixTreeNode[] => list.flatMap(node => {

    const text = getSearchText(node).toLowerCase();
    const selfMatches = text.includes(query);
    const children = resolveChildren(node, cache);
    const lazy = isLazyUnloaded(node, cache);

    if (!children) {

        if (lazy || selfMatches) return [node];
        return [];

    }

    const filteredChildren = filterTree(children, cache, query, getSearchText, expandIds);
    if (filteredChildren.length === 0 && !selfMatches) return [];

    if (filteredChildren.length > 0) expandIds.add(node.id);
    return [{ ...node, children: filteredChildren.length > 0 ? filteredChildren : children }];

});

const TriCheck = memo((props: TriCheckProps) => {

    const { name, disabled, label, onChange, state, type } = props;

    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {

        if (ref.current && type === 'checkbox') ref.current.indeterminate = state === 'indeterminate';

    }, [state, type]);

    return (
        <span className="gmetrix-tree-view__check">
            <input
                onKeyDown={e => {

                    if (e.key === 'Enter') {

                        e.preventDefault();
                        if (!disabled) onChange();

                    }

                }}
                aria-label={label}
                checked={state === 'checked'}
                className="form-check-input m-0"
                disabled={disabled}
                name={name}
                onChange={onChange}
                ref={ref}
                type={type}
            />
        </span>
    );

});

TriCheck.displayName = 'GMetrixTreeView.TriCheck';

const TreeRow = memo((props: TreeRowProps) => {

    const {
        children,
        controlDisabled,
        controlType,
        depth,
        expandable,
        expanded,
        label,
        labelInert,
        labelText,
        onCheck,
        onToggle,
        radioGroupName,
        selectedCount,
        selectedCountAlwaysShow,
        state
    } = props;

    const { formatMessage } = useIntl();

    const indentDepth = Math.min(depth, MAX_INDENT_DEPTH);

    const handleLabelClick = () => {

        if (labelInert) return;
        if (controlType !== 'none' && !controlDisabled && onCheck) onCheck();
        else if (expandable && onToggle) onToggle();

    };

    const showBadge = Boolean(
        selectedCount && selectedCount > 0
        && (selectedCountAlwaysShow || (expandable && !expanded))
    );
    const badgeInline = showBadge && selectedCountAlwaysShow;

    return (
        <div className={`gmetrix-tree-view__node gmetrix-tree-view__node--depth-${indentDepth}`}>
            <div className={`gmetrix-tree-view__row${badgeInline ? ' gmetrix-tree-view__row--badge-inline' : ''}`}>
                {expandable ? (
                    <button
                        aria-label={expanded ? formatMessage({ id: 'collapse.db64db2a', defaultMessage: 'Collapse' }) : formatMessage({ id: 'expand.130d2b72', defaultMessage: 'Expand' })}
                        className="gmetrix-tree-view__chevron"
                        onClick={onToggle}
                        type="button"
                    >
                        <i className={`fa-solid ${expanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                    </button>
                ) : (
                    <span aria-hidden className="gmetrix-tree-view__chevron gmetrix-tree-view__chevron--placeholder" />
                )}

                {controlType === 'none' ? null : (
                    <TriCheck
                        disabled={controlDisabled}
                        label={labelText}
                        name={controlType === 'radio' ? radioGroupName : undefined}
                        onChange={onCheck ?? (() => undefined)}
                        state={state}
                        type={controlType}
                    />
                )}

                <span
                    className="gmetrix-tree-view__label"
                    onClick={labelInert ? undefined : handleLabelClick}
                >
                    {label}
                </span>
                {showBadge ? (
                    <span aria-label={formatMessage({ id: 'count_selected.8d3094c5', defaultMessage: '{count} selected' }, { count: selectedCount })} className="gmetrix-tree-view__badge">
                        {selectedCount}
                    </span>
                ) : null}
            </div>
            {expandable && expanded ? children : null}
        </div>
    );

});

TreeRow.displayName = 'GMetrixTreeView.TreeRow';

let radioNameSeed = 0;

export const GMetrixTreeView = (props: GMetrixTreeViewProps) => {

    const {
        className,
        defaultExpandedIds,
        emptyMessage,
        getSearchText = defaultGetSearchText,
        loadChildren,
        loadingDelayMs = DEFAULT_LOADING_DELAY_MS,
        maxHeight = '60vh',
        nodes,
        noResultsMessage,
        onSelectionChange,
        renderLoadErrorNode,
        renderLoadingNode,
        searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
        searchPlaceholder,
        selectAllLabel,
        selectedIds,
        selectionMode = 'multiple',
        showSearch = false,
        showSelectAll = false
    } = props;

    const { formatMessage } = useIntl();

    const [state, setState] = useSuperState({
        debouncedQuery: '',
        expanded: undefined as Set<TreeNodeId> | undefined,
        loadCache: new Map<TreeNodeId, LoadState>(),
        loadingVisible: new Set<TreeNodeId>(),
        pendingIntent: new Map<TreeNodeId, boolean>(),
        searchInput: ''
    });

    const { debouncedQuery, expanded, loadCache, loadingVisible, pendingIntent, searchInput } = state;

    const radioGroupNameRef = useRef<string>();
    if (!radioGroupNameRef.current) {

        radioNameSeed += 1;
        radioGroupNameRef.current = `gmtv-radios-${radioNameSeed}`;

    }

    const loadingTimersRef = useRef<Map<TreeNodeId, ReturnType<typeof setTimeout>>>(new Map());
    const inFlightRef = useRef<Set<TreeNodeId>>(new Set());
    const seededExpansionRef = useRef<Set<TreeNodeId>>(new Set());
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => () => {

        loadingTimersRef.current.forEach(t => clearTimeout(t));
        loadingTimersRef.current.clear();

    }, []);

    const selectedSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);

    const isMulti = selectionMode === 'multiple';
    const isSingle = selectionMode === 'single';
    const isLeafSingle = selectionMode === 'leaf-single';
    const showCheckboxes = isMulti;
    const showRadios = isSingle || isLeafSingle;
    const noSelection = selectionMode === 'none';

    const effectiveShowSelectAll = showSelectAll && isMulti;

    if (showSelectAll && !isMulti && process.env.NODE_ENV !== 'production') {

        // eslint-disable-next-line no-console
        console.warn('[GMetrixTreeView] showSelectAll is ignored when selectionMode is not "multiple".');

    }

    const defaultExpandedSet = useMemo(() => {

        if (defaultExpandedIds) return new Set<TreeNodeId>(defaultExpandedIds);

        const result = new Set<TreeNodeId>();
        if (effectiveShowSelectAll) result.add(ROOT_KEY);
        const walk = (list: GMetrixTreeNode[]) => list.forEach(n => {

            const c = resolveChildren(n, loadCache);
            if (c && c.length) {

                result.add(n.id);
                walk(c);

            }

        });
        walk(nodes);
        return result;

    }, [nodes, loadCache, defaultExpandedIds, effectiveShowSelectAll]);

    useEffect(() => {

        if (!expanded) {

            const initial = new Set(defaultExpandedSet);
            initial.forEach(id => seededExpansionRef.current.add(id));
            setState({ expanded: initial });
            return;

        }

        if (defaultExpandedIds) return;

        let changed = false;
        const next = new Set(expanded);
        defaultExpandedSet.forEach(id => {

            if (seededExpansionRef.current.has(id)) return;
            seededExpansionRef.current.add(id);
            if (!next.has(id)) {

                next.add(id);
                changed = true;

            }

        });
        if (changed) setState({ expanded: next });

    }, [defaultExpandedSet]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {

        if (searchInput === debouncedQuery) return;
        const timer = setTimeout(() => setState({ debouncedQuery: searchInput }), searchDebounceMs);
        return () => clearTimeout(timer);

    }, [searchInput, searchDebounceMs]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {

        if (!showSearch) return;
        if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;

    }, [debouncedQuery, showSearch]);

    const trimmedQuery = debouncedQuery.trim().toLowerCase();
    const isSearching = showSearch && trimmedQuery.length > 0;

    const { filteredNodes, searchExpandIds } = useMemo(() => {

        if (!isSearching) return { filteredNodes: nodes, searchExpandIds: new Set<TreeNodeId>() };
        const expandIds = new Set<TreeNodeId>();
        const filtered = filterTree(nodes, loadCache, trimmedQuery, getSearchText, expandIds);
        return { filteredNodes: filtered, searchExpandIds: expandIds };

    }, [isSearching, nodes, loadCache, trimmedQuery, getSearchText]);

    const expandedSet = expanded ?? defaultExpandedSet;
    const effectiveExpandedSet = useMemo(() => {

        if (!isSearching) return expandedSet;
        const merged = new Set(expandedSet);
        searchExpandIds.forEach(id => merged.add(id));
        if (effectiveShowSelectAll) merged.add(ROOT_KEY);
        return merged;

    }, [isSearching, expandedSet, searchExpandIds, effectiveShowSelectAll]);

    const allKnownLeafIds = useMemo(() => {

        const out: TreeNodeId[] = [];
        nodes.forEach(n => collectLoadedLeafIds(n, loadCache, out));
        return out;

    }, [nodes, loadCache]);

    const computeNodeState = (node: GMetrixTreeNode): CheckState => {

        if (noSelection) return 'unchecked';

        if (showRadios) return selectedSet.has(node.id) ? 'checked' : 'unchecked';

        const ids: TreeNodeId[] = [];
        collectAllKnownIds(node, loadCache, ids);
        if (ids.length === 0) return selectedSet.has(node.id) ? 'checked' : 'unchecked';

        let on = 0;
        ids.forEach(id => {

            if (selectedSet.has(id)) on += 1;

        });
        if (on === 0) return 'unchecked';
        if (on === ids.length) return 'checked';
        return 'indeterminate';

    };

    const rootState: CheckState = useMemo(() => {

        if (!effectiveShowSelectAll) return 'unchecked';
        if (allKnownLeafIds.length === 0) return 'unchecked';
        let on = 0;
        allKnownLeafIds.forEach(id => {

            if (selectedSet.has(id)) on += 1;

        });
        if (on === 0) return 'unchecked';
        if (on === allKnownLeafIds.length) return 'checked';
        return 'indeterminate';

    }, [effectiveShowSelectAll, allKnownLeafIds, selectedSet]);

    const toggleExpand = (id: TreeNodeId) => {

        const next = new Set(expandedSet);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setState({ expanded: next });

    };

    const startLoad = async (node: GMetrixTreeNode, onLoaded?: (children: GMetrixTreeNode[]) => void) => {

        if (!loadChildren) return;
        if (inFlightRef.current.has(node.id)) return;

        inFlightRef.current.add(node.id);
        setState({ loadCache: new Map(loadCache).set(node.id, { status: 'loading' }) });

        const timer = setTimeout(() => {

            const visible = new Set(loadingVisible);
            visible.add(node.id);
            setState({ loadingVisible: visible });

        }, loadingDelayMs);
        loadingTimersRef.current.set(node.id, timer);

        try {

            const children = await loadChildren(node);
            clearTimeout(timer);
            loadingTimersRef.current.delete(node.id);

            const nextVisible = new Set(loadingVisible);
            nextVisible.delete(node.id);
            const nextCache = new Map(loadCache).set(node.id, { children, status: 'loaded' });
            setState({ loadCache: nextCache, loadingVisible: nextVisible });

            if (onLoaded) onLoaded(children);

        } catch (e) {

            clearTimeout(timer);
            loadingTimersRef.current.delete(node.id);
            const error = e instanceof Error ? e : new Error(String(e));

            const nextVisible = new Set(loadingVisible);
            nextVisible.delete(node.id);
            const nextCache = new Map(loadCache).set(node.id, { error, status: 'error' });
            const nextIntent = new Map(pendingIntent);
            nextIntent.delete(node.id);
            setState({ loadCache: nextCache, loadingVisible: nextVisible, pendingIntent: nextIntent });

        } finally {

            inFlightRef.current.delete(node.id);

        }

    };

    const handleExpandToggle = (node: GMetrixTreeNode) => {

        toggleExpand(node.id);
        if (isLazyUnloaded(node, loadCache)) startLoad(node);

    };

    const applySelectionDelta = (idsToToggle: TreeNodeId[], turnOn: boolean) => {

        if (!onSelectionChange) return;
        const next = new Set(selectedSet);
        if (turnOn) idsToToggle.forEach(id => next.add(id));
        else idsToToggle.forEach(id => next.delete(id));
        onSelectionChange(Array.from(next));

    };

    const replaceSelectionWith = (id: TreeNodeId | null) => {

        if (!onSelectionChange) return;
        onSelectionChange(id === null ? [] : [id]);

    };

    const handleMultiNodeCheck = (node: GMetrixTreeNode) => {

        const leafIds: TreeNodeId[] = [];
        collectLoadedLeafIds(node, loadCache, leafIds);

        const currentState = computeNodeState(node);
        const turnOn = currentState !== 'checked';

        if (leafIds.length > 0) applySelectionDelta(leafIds, turnOn);

        const lazyParents: GMetrixTreeNode[] = [];
        const findLazy = (n: GMetrixTreeNode) => {

            if (isLazyUnloaded(n, loadCache)) {

                lazyParents.push(n);
                return;

            }
            const kids = resolveChildren(n, loadCache);
            if (kids) kids.forEach(findLazy);

        };
        findLazy(node);

        if (lazyParents.length > 0) {

            const nextIntent = new Map(pendingIntent);
            lazyParents.forEach(p => nextIntent.set(p.id, turnOn));
            setState({ pendingIntent: nextIntent });

            lazyParents.forEach(p => startLoad(p, children => {

                const newLeafIds: TreeNodeId[] = [];
                children.forEach(c => collectLoadedLeafIds(c, loadCache, newLeafIds));
                if (newLeafIds.length > 0) applySelectionDelta(newLeafIds, turnOn);

            }));

        }

    };

    const handleSelectAll = () => {

        const turnOn = rootState !== 'checked';

        if (allKnownLeafIds.length > 0) applySelectionDelta(allKnownLeafIds, turnOn);

        const lazyParents = nodes.filter(n => isLazyUnloaded(n, loadCache));
        if (lazyParents.length > 0) {

            const nextIntent = new Map(pendingIntent);
            lazyParents.forEach(p => nextIntent.set(p.id, turnOn));
            setState({ pendingIntent: nextIntent });

            lazyParents.forEach(p => startLoad(p, children => {

                const newLeafIds: TreeNodeId[] = [];
                children.forEach(c => collectLoadedLeafIds(c, loadCache, newLeafIds));
                if (newLeafIds.length > 0) applySelectionDelta(newLeafIds, turnOn);

            }));

        }

    };

    const handleSingleSelect = (node: GMetrixTreeNode, isLeaf: boolean) => {

        if (isLeafSingle && !isLeaf) return;
        replaceSelectionWith(node.id);

    };

    const retryLoad = (node: GMetrixTreeNode) => {

        const nextCache = new Map(loadCache);
        nextCache.delete(node.id);
        setState({ loadCache: nextCache });
        startLoad(node);

    };

    const renderNodes = (list: GMetrixTreeNode[], depth: number): React.ReactNode => list.map(node => {

        const children = resolveChildren(node, loadCache);
        const lazyUnloaded = isLazyUnloaded(node, loadCache);
        const expandable = Boolean(children && children.length > 0) || lazyUnloaded;
        const isLeaf = !expandable;
        const cacheEntry = loadCache.get(node.id);
        const showLoading = cacheEntry?.status === 'loading' && loadingVisible.has(node.id);
        const showError = cacheEntry?.status === 'error';
        const errorObj = cacheEntry?.status === 'error' ? cacheEntry.error : undefined;

        const labelText = typeof node.label === 'string' ? node.label : String(node.id);

        const controlType: 'checkbox' | 'radio' | 'none' =
            noSelection ? 'none'
                : showCheckboxes ? 'checkbox'
                    : isLeaf ? 'radio'
                        : 'none';

        const controlDisabled = false;

        const onCheck = noSelection
            ? undefined
            : showCheckboxes
                ? () => handleMultiNodeCheck(node)
                : isLeaf
                    ? () => handleSingleSelect(node, isLeaf)
                    : undefined;

        const childIndentDepth = Math.min(depth + 1, MAX_INDENT_DEPTH);

        let selectedCount: number | undefined;
        if (showCheckboxes && expandable) {

            const subtreeLeafIds: TreeNodeId[] = [];
            collectLoadedLeafIds(node, loadCache, subtreeLeafIds);
            let count = 0;
            subtreeLeafIds.forEach(id => {

                if (selectedSet.has(id)) count += 1;

            });
            selectedCount = count;

        }

        return (
            <TreeRow
                controlDisabled={controlDisabled}
                controlType={controlType}
                depth={depth}
                expandable={expandable}
                expanded={effectiveExpandedSet.has(node.id)}
                key={node.id}
                label={node.label}
                labelInert={noSelection}
                labelText={labelText}
                onCheck={onCheck}
                onToggle={expandable ? () => handleExpandToggle(node) : undefined}
                radioGroupName={radioGroupNameRef.current}
                selectedCount={selectedCount}
                state={computeNodeState(node)}
            >
                {children && children.length > 0 ? renderNodes(children, depth + 1) : null}
                {showLoading ? (
                    <div className={`gmetrix-tree-view__node gmetrix-tree-view__node--depth-${childIndentDepth}`}>
                        <div className="gmetrix-tree-view__row text-muted">
                            {renderLoadingNode ? renderLoadingNode(node) : (
                                <>
                                    <span aria-hidden className="gmetrix-tree-view__chevron gmetrix-tree-view__chevron--placeholder" />
                                    <i aria-hidden className="fa-solid fa-spinner fa-spin me-2" />
                                    <span>
                                        <FormattedMessage defaultMessage="Loading..." description={formatMessage({ id: 'mensaje.3e3d855e', defaultMessage: 'Mensaje' })} id="loading.2350c7f9" />
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                ) : null}
                {showError && errorObj ? (
                    <div className={`gmetrix-tree-view__node gmetrix-tree-view__node--depth-${childIndentDepth}`}>
                        <div className="gmetrix-tree-view__row text-danger">
                            {renderLoadErrorNode ? renderLoadErrorNode(node, errorObj, () => retryLoad(node)) : (
                                <>
                                    <span aria-hidden className="gmetrix-tree-view__chevron gmetrix-tree-view__chevron--placeholder" />
                                    <i aria-hidden className="fa-solid fa-triangle-exclamation me-2" />
                                    <span className="me-2">
                                        <FormattedMessage defaultMessage="Failed" description={formatMessage({ id: 'formulario.6dc35e9d', defaultMessage: 'Formulario' })} id="failed.8e421c91" />
                                    </span>
                                    <button
                                        className="btn btn-link btn-sm p-0"
                                        onClick={() => retryLoad(node)}
                                        type="button"
                                    >
                                        <FormattedMessage defaultMessage="Retry" id="retry.225ca180" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ) : null}
            </TreeRow>
        );

    });

    const isEmpty = nodes.length === 0;
    const isSearchEmpty = isSearching && filteredNodes.length === 0;

    const rootLabel = selectAllLabel
        ?? (rootState === 'checked' ? formatMessage({ id: 'deselect_all.da9ca1dd', defaultMessage: 'Deselect All' }) : formatMessage({ id: 'select_all.dd487b25', defaultMessage: 'Select All' }));

    const placeholder = searchPlaceholder ?? `${formatMessage({ id: 'search.0ab06b2c', defaultMessage: 'Search' })}...`;

    return (
        <div className={`gmetrix-tree-view-wrapper ${className ?? ''}`}>
            {showSearch ? (
                <div className="gmetrix-tree-view__search">
                    <GMetrixInput
                        aria-label={placeholder}
                        className="w-100"
                        onChange={e => setState({ searchInput: (e.target as HTMLInputElement).value })}
                        placeholder={placeholder}
                        value={searchInput}
                    />
                </div>
            ) : null}
            <div
                className={[
                    'gmetrix-tree-view',
                    effectiveShowSelectAll ? 'gmetrix-tree-view--has-select-all' : '',
                    noSelection ? 'gmetrix-tree-view--no-selection' : ''
                ].filter(Boolean).join(' ')}
                ref={scrollContainerRef}
                style={{ maxHeight }}
            >
                {isEmpty ? (
                    <div className="gmetrix-tree-view__empty text-muted p-3">
                        {emptyMessage ?? null}
                    </div>
                ) : isSearchEmpty ? (
                    <div className="gmetrix-tree-view__empty text-muted p-3">
                        {noResultsMessage ?? <FormattedMessage defaultMessage="No results were found" description={formatMessage({ id: 'gridview.a489116f', defaultMessage: 'Gridview' })} id="no_results_were_found.53f5818d" />}
                    </div>
                ) : effectiveShowSelectAll ? (
                    <TreeRow
                        expandable
                        selectedCountAlwaysShow
                        controlType="checkbox"
                        depth={0}
                        expanded={effectiveExpandedSet.has(ROOT_KEY)}
                        label={rootLabel}
                        labelText={typeof rootLabel === 'string' ? rootLabel : formatMessage({ id: 'select_all.c184be4d', defaultMessage: 'Select all' })}
                        onCheck={handleSelectAll}
                        onToggle={() => toggleExpand(ROOT_KEY)}
                        selectedCount={allKnownLeafIds.reduce<number>((n, id) => n + (selectedSet.has(id) ? 1 : 0), 0)}
                        state={rootState}
                    >
                        {renderNodes(filteredNodes, 1)}
                    </TreeRow>
                ) : (
                    renderNodes(filteredNodes, 0)
                )}
            </div>
        </div>
    );

};
